import { gql, useMutation, useQuery } from "@apollo/client";
import { ChevronLeftIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import {
  CaskStatus,
  GetCask,
  GetCaskVariables,
  GetCask_adminScope_casks_nodes_caskUsers,
  UserType,
} from "__generated__/graphql";
import DataTable from "components/DataTable";
import { useRef, useState } from "react";
import { RiUpload2Fill } from "react-icons/ri";
import { useHistory, useParams } from "react-router";
import UploadFileService from "services/upload-file-service";
import { formatDate } from "utils/dates";
import CaskOrdersTable from "./CaskOrdersTable";
import DownloadCaskProofButton from "./DownloadCaskProofButton";
import VismaOrderHistoryTable from "./VismaOrderHistoryTable";

const GET_CASK = gql`
  query GetCask($caskId: Int!) {
    adminScope {
      casks(first: 1, where: { and: { id: { eq: $caskId } } }) {
        nodes {
          id
          vismaId
          vismaCaskNumber
          caskSign
          status
          notRefillable
          caskTypeText
          recipeText
          volumeFilled
          stockNumber
          loanClaim
          alcoholPercentage
          filled
          liquorFilled
          percentSmoked
          warehouseArea
          timesRefilled
          currentVolume
          loadVolume
          lastSyncedWithVisma
          imageThumb
          thumbImage
          fullImage
          recipe {
            id
            recipeInformation {
              language
              recipeId
              name
              description
            }
          }
          caskType {
            vismaId
            caskTypeInformation {
              language
              name
              description
            }
          }
          caskUsers {
            id
            userType
            vismaUser {
              vismaUserId
              vismaCustomerNumber
              phoneNumber
              lastSyncedWithVisma
            }
            user {
              firstName
              lastName
              email
            }
          }
        }
      }
    }
  }
`;

const REMOVE_CASK_MEMBER = gql`
  mutation RemoveCaskMember($caskId: Int!, $caskUserId: Int!) {
    removeCaskMember(caskId: $caskId, caskUserId: $caskUserId)
  }
`;

const columnHelper =
  createColumnHelper<GetCask_adminScope_casks_nodes_caskUsers>();

const mapCaskStatus = (status: CaskStatus) => {
  switch (status) {
    case CaskStatus.REMOVED:
      return "Avbruten";
    case CaskStatus.FILLED:
      return "Fylld";
    case CaskStatus.FULL_BOTTLED:
      return "Buteljerad";
    case CaskStatus.FULL_BOTTLING_ORDERED:
      return "Buteljering beställd";
    case CaskStatus.NOT_FILLED:
      return "Tom";

    default:
      return "Okänd";
  }
};

const CaskPage = () => {
  const toast = useToast();
  const [fullImage, toggleImageSize] = useState<boolean>(false);
  const { caskId } = useParams<{ caskId: string }>();
  const { loading, data, refetch } = useQuery<GetCask, GetCaskVariables>(
    GET_CASK,
    {
      variables: { caskId: Number(caskId) },
    }
  );
  const cask = data?.adminScope.casks?.nodes![0]!;
  const history = useHistory();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedCaskUser, setSelectedCaskUser] =
    useState<GetCask_adminScope_casks_nodes_caskUsers | null>(null);

  const [removeCaskMember, { loading: removing }] = useMutation(
    REMOVE_CASK_MEMBER,
    {
      onCompleted: () => {
        toast({
          title: "Användare frånkopplad",
          description: "Användaren har tagits bort från fatet.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose();
        refetch();
      },
      onError: (error) => {
        toast({
          title: "Fel vid borttagning",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    }
  );

  const handleRemoveCaskMember = (
    caskUser: GetCask_adminScope_casks_nodes_caskUsers
  ) => {
    setSelectedCaskUser(caskUser);
    onOpen();
  };

  const confirmRemoveCaskMember = () => {
    if (selectedCaskUser) {
      removeCaskMember({
        variables: {
          caskId: cask.id,
          caskUserId: selectedCaskUser.id,
        },
      });
    }
  };

  const columns = [
    columnHelper.accessor("userType", {
      cell: (info) => (info.getValue() === "OWNER" ? "Ägare" : "Delägare"),
      header: "Typ",
    }),
    columnHelper.accessor("vismaUser", {
      cell: (info) => info.getValue()?.vismaUserId,
      header: "Visma-id",
    }),
    columnHelper.accessor("vismaUser", {
      cell: (info) => info.getValue()?.vismaCustomerNumber,
      header: "Visma-Kundnummer",
    }),
    columnHelper.accessor("user", {
      cell: (info) =>
        `${info.getValue()?.firstName} ${info.getValue()?.lastName}`,
      header: "Namn",
    }),
    columnHelper.accessor("user.email", {
      cell: (info) => info.getValue(),
      header: "E-post",
    }),
    columnHelper.accessor("vismaUser", {
      cell: (info) => info.getValue()?.phoneNumber,
      header: "Telefon",
    }),
    columnHelper.accessor("vismaUser", {
      cell: (info) => formatDate(info.getValue()?.lastSyncedWithVisma || ""),
      header: "Synkades",
    }),
    columnHelper.display({
      id: "actions",
      cell: (info) => (
        <Button
          bg="white"
          variant="outline"
          onClick={() => handleRemoveCaskMember(info.row.original)}
        >
          <DeleteIcon />
        </Button>
      ),
      header: "",
    }),
  ];

  const onFileChanged = async (evt: React.SyntheticEvent<HTMLInputElement>) => {
    const target = evt.target as HTMLInputElement;

    if (target.files === null) {
      return;
    }

    toast({
      id: cask.id,
      title: "Fatbild laddas upp.",
      description: `Fatbilden för fatet laddas upp.`,
      status: "loading",
      duration: 20000,
    });

    UploadFileService.uploadCaskImage(target.files[0], cask!.id).then(
      async () => {
        toast.update(cask.id, {
          title: "Fatbild laddades upp.",
          description: `Fatbilden för fatet laddades upp.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        await refetch();
      }
    );
  };

  if (loading) return <Spinner />;

  return (
    <>
      <Box mb={4}>
        <Button
          mb={4}
          leftIcon={<ChevronLeftIcon />}
          variant="outline"
          onClick={() => history.goBack()}
        >
          Tillbaka
        </Button>
      </Box>
      <Heading size="lg" mb={4}>
        {`Fat ${cask.vismaId}`}
      </Heading>
      <Box mb={4} bg="gray.100" p={4} borderRadius={4}>
        <StatGroup mb={2}>
          <Stat>
            <StatLabel>Visma-id</StatLabel>
            <StatNumber fontSize="lg">{cask.vismaId}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Fatnummer</StatLabel>
            <StatNumber fontSize="lg">{cask.vismaCaskNumber}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Fatskylt</StatLabel>
            <StatNumber fontSize="lg">{cask.caskSign}</StatNumber>
          </Stat>
        </StatGroup>
        <StatGroup mb={2}>
          <Stat>
            <StatLabel>Lagerplats</StatLabel>
            <StatNumber fontSize="lg">{cask.stockNumber}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Lagerhus</StatLabel>
            <StatNumber fontSize="lg">{cask.warehouseArea}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Synkades</StatLabel>
            <StatNumber fontSize="lg">
              {formatDate(cask.lastSyncedWithVisma || "")}
            </StatNumber>
          </Stat>
        </StatGroup>
        <StatGroup mb={2}>
          <Stat>
            <StatLabel>Status</StatLabel>
            <StatNumber fontSize="lg">{mapCaskStatus(cask.status)}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Tillgänglig för refill</StatLabel>
            <StatNumber fontSize="lg">
              {cask.notRefillable ? "Nej" : "Ja"}
            </StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Fattyp</StatLabel>
            <StatNumber fontSize="lg">{cask.caskTypeText}</StatNumber>
          </Stat>
        </StatGroup>
        <StatGroup mb={2}>
          <Stat>
            <StatLabel>Lånekrav</StatLabel>
            <StatNumber fontSize="lg">{cask.loanClaim}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Återfylld antal gånger</StatLabel>
            <StatNumber fontSize="lg">{cask.timesRefilled}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Alkoholhalt</StatLabel>
            <StatNumber fontSize="lg">{cask.alcoholPercentage}</StatNumber>
          </Stat>
        </StatGroup>
        <StatGroup mb={2}>
          <Stat>
            <StatLabel>Fatägarbevis</StatLabel>
            <StatNumber>
              <Flex gap={2} mt={1}>
                <DownloadCaskProofButton caskId={cask.id} language={"sv"} />
                <DownloadCaskProofButton caskId={cask.id} language={"en"} />
              </Flex>
            </StatNumber>
          </Stat>
        </StatGroup>
      </Box>
      <Heading size="lg" mb={2}>
        Fatbild
      </Heading>
      <Box mb={4} bg="gray.100" p={4} borderRadius={4}>
        <Box>
          {cask.imageThumb ? (
            <Image
              onClick={() => toggleImageSize(!fullImage)}
              src={fullImage ? cask.fullImage! : cask.thumbImage!}
              cursor="pointer"
            />
          ) : (
            <Text fontSize="lg">Fatet saknar bild</Text>
          )}
          <Input
            hidden
            ref={fileInputRef}
            type="file"
            accept="image/x-png,image/gif,image/jpeg"
            onChange={(e) => onFileChanged(e)}
          />
          <Button
            mt={4}
            bg="white"
            size={"lg"}
            rightIcon={<RiUpload2Fill size="1.25rem" />}
            onClick={() => fileInputRef.current?.click()}
          >
            {cask.imageThumb ? "Ändra fatbild" : "Lägg till fatbild"}
          </Button>
        </Box>
      </Box>
      <Box p={4}>
        <Heading size="lg" mb={2}>
          Fatägare
        </Heading>
        <DataTable
          data={cask.caskUsers as GetCask_adminScope_casks_nodes_caskUsers[]}
          columns={columns}
        />
      </Box>
      {cask.id && <CaskOrdersTable caskId={cask.id} />}
      {cask.id && <VismaOrderHistoryTable caskId={cask.id} />}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Koppla bort fatägare</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="lg" mb={4}>
              Är du säker på att du vill koppla bort följande användare från
              fatet?
            </Text>
            {selectedCaskUser?.userType === UserType.OWNER && (
              <Text fontWeight="bold" fontSize="lg" mb={4}>
                Notera att borttag av ägare inte synkas till Visma. Användaren
                måste tas bort manuellt i Visma.
              </Text>
            )}
            {selectedCaskUser && (
              <Box bg="gray.50" p={4} borderRadius="md">
                <Stat mb={2}>
                  <StatLabel>Namn</StatLabel>
                  <StatNumber fontSize="md">
                    {selectedCaskUser.user?.firstName}{" "}
                    {selectedCaskUser.user?.lastName}
                  </StatNumber>
                </Stat>
                <Stat mb={2}>
                  <StatLabel>E-post</StatLabel>
                  <StatNumber fontSize="md">
                    {selectedCaskUser.user?.email}
                  </StatNumber>
                </Stat>
                <Stat mb={2}>
                  <StatLabel>Typ</StatLabel>
                  <StatNumber fontSize="md">
                    {selectedCaskUser.userType === "OWNER"
                      ? "Ägare"
                      : "Delägare"}
                  </StatNumber>
                </Stat>
                {selectedCaskUser.vismaUser && (
                  <>
                    <Stat mb={2}>
                      <StatLabel>Visma-id</StatLabel>
                      <StatNumber fontSize="md">
                        {selectedCaskUser.vismaUser.vismaUserId}
                      </StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Visma-kundnummer</StatLabel>
                      <StatNumber fontSize="md">
                        {selectedCaskUser.vismaUser.vismaCustomerNumber}
                      </StatNumber>
                    </Stat>
                  </>
                )}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Avbryt
            </Button>
            <Button
              colorScheme="red"
              onClick={confirmRemoveCaskMember}
              isLoading={removing}
            >
              Ta bort
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CaskPage;
